import VueI18n from 'vue-i18n';

/**
 * Service to translate backend response data that contains i18n keys.
 *
 * This service processes various data structures from the Fargo backend where
 * text fields contain i18n keys directly, enabling proper internationalization.
 * It handles charts, tables, and other data structures.
 *
 * Expected backend format examples:
 *
 * Charts:
 * {
 *   "options": {
 *     "title": {
 *       "text": "businessanalytics.sales.invoice.sales.reps.situation.chart.title"
 *     },
 *     "subtitle": {
 *       "text": "currency",
 *       "params": { "currency": "EUR" }
 *     },
 *     "labels": [
 *       "open.orders.short",
 *       "billed"
 *     ]
 *   }
 * }
 *
 * Tables:
 * {
 *   "headers": [
 *     { "text": "customer.code", "value": "customer_code", "align": "start" },
 *     { "text": "customer.description", "value": "customer_description", "align": "start" }
 *   ],
 *   "data": [...]
 * }
 */
export default class BackendI18nService {
  private i18n: VueI18n;

  constructor(i18n: VueI18n) {
    this.i18n = i18n;
  }

  /**
   * Translates a chart response object from Fargo backend.
   * Processes title, subtitle, labels, and any other i18nKey fields.
   *
   * @param chartData The chart data object from backend
   * @returns The chart data with translated text
   */
  public translateChartData(chartData: any): any {
    if (!chartData) {
      return chartData;
    }

    // Deep clone to avoid mutating original
    const translated = JSON.parse(JSON.stringify(chartData));

    // Translate options object if present
    if (translated.options) {
      this.translateObject(translated.options);
    }

    return translated;
  }

  /**
   * Recursively translates an object that may contain i18nKey fields.
   *
   * @param obj The object to translate
   */
  private translateObject(obj: any): void {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        // If array element is a string that looks like an i18n key, translate it
        if (typeof obj[i] === 'string' && this.looksLikeI18nKey(obj[i])) {
          obj[i] = this.i18n.t(obj[i]) as string;
        } else if (typeof obj[i] === 'object') {
          this.translateObject(obj[i]);
        }
      }
      return;
    }

    // Handle objects with text field that contains an i18n key
    if (obj.text && typeof obj.text === 'string' && this.looksLikeI18nKey(obj.text)) {
      const key = obj.text;
      const params = obj.params || {};

      // Translate the i18n key
      obj.text = this.i18n.t(key, params) as string;

      // Clean up params after translation
      delete obj.params;
    }

    // Recursively process all object properties
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        if (typeof value === 'object') {
          this.translateObject(value);
        } else if (typeof value === 'string' && this.looksLikeI18nKey(value)) {
          // Direct string value that looks like i18n key
          obj[key] = this.i18n.t(value) as string;
        }
      }
    }
  }

  /**
   * Checks if a string looks like an i18n key.
   * I18n keys typically start with lowercase and contain dots.
   *
   * @param str The string to check
   * @returns true if it looks like an i18n key
   */
  private looksLikeI18nKey(str: string): boolean {
    // Check if string matches pattern: word.word.word (at least 2 segments)
    return /^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9]+)+$/.test(str);
  }

  /**
   * Translates just the labels array from a chart response.
   * This is a convenience method for cases where only labels need translation.
   *
   * @param labels Array of label strings (may be i18n keys)
   * @returns Translated labels array
   */
  public translateLabels(labels: string[]): string[] {
    if (!labels || !Array.isArray(labels)) {
      return labels;
    }

    return labels.map(label => {
      if (typeof label === 'string' && this.looksLikeI18nKey(label)) {
        return this.i18n.t(label) as string;
      }
      return label;
    });
  }

  /**
   * Translates Vuetify table headers where text fields contain i18n keys.
   * Handles both simple string arrays and header objects with text/value/align properties.
   *
   * @param headers Array of header objects or strings
   * @returns Translated headers array
   */
  public translateTableHeaders(headers: any[]): any[] {
    if (!headers || !Array.isArray(headers)) {
      return headers;
    }

    return headers.map(header => {
      // If header is a string, translate it directly
      if (typeof header === 'string' && this.looksLikeI18nKey(header)) {
        return this.i18n.t(header) as string;
      }

      // If header is an object, clone it and translate the text property
      if (typeof header === 'object' && header !== null) {
        const translatedHeader = { ...header };

        if (translatedHeader.text && typeof translatedHeader.text === 'string' && this.looksLikeI18nKey(translatedHeader.text)) {
          translatedHeader.text = this.i18n.t(translatedHeader.text) as string;
        }

        return translatedHeader;
      }

      return header;
    });
  }

  /**
   * Translates a single title/subtitle object where text contains an i18n key.
   *
   * @param titleObj Object with text (i18n key) and optional params
   * @returns Object with translated text
   */
  public translateTitle(titleObj: any): any {
    if (!titleObj) {
      return titleObj;
    }

    if (titleObj.text && typeof titleObj.text === 'string' && this.looksLikeI18nKey(titleObj.text)) {
      const translated = { ...titleObj };
      const key = translated.text;
      const params = translated.params || {};

      translated.text = this.i18n.t(key, params) as string;
      delete translated.params;

      return translated;
    }

    return titleObj;
  }
}
